import _ from 'lodash';
import {
  isLive,
  isEpisode,
  determineErrorFromResponse,
  isStaticRender,
} from 'helpers/ContentHelper';
import {
  LIVE,
  PLAYER_CONTEXT_LIVE,
  PLAYER_CONTEXT_WATCH,
  PLAYER_TYPE_DEFAULT,
  VOD,
} from 'helpers/PlayerHelper';
import { userAttributeEvents as optimizelyUserAttributeEvents } from 'helpers/OptimizelyHelper';

import {
  CLEAR_GLOBAL_LIVE_RESTART,
  CLEAR_GLOBAL_NEXT_VIDEOS_VALID_FOR,
  CLEAR_GLOBAL_PERCENT_WATCHED,
  CLEAR_GLOBAL_VIDEO,
  FETCH_GLOBAL_VIDEO,
  FETCH_GLOBAL_VIDEO_NEXT_VIDEO,
  FETCH_GLOBAL_VIDEO_PANEL,
  FETCH_GLOBAL_VIDEO_UP_NEXT,
  HANDLE_VOD_SECTION_CLICKED,
  PLAY_GLOBAL_VIDEO_NEXT_PLAYLIST_VIDEO,
  RESET_VOD_SECTION_CLICKED,
  SAVE_GLOBAL_PERCENT_WATCHED,
  SET_GLOBAL_LIVE_RESTART,
  SET_GLOBAL_PORTAL_REF,
  SET_GLOBAL_PORTAL_CONTENT_FINISHED,
  SET_GLOBAL_VIDEO_DISABLE_RENDER,
  SET_GLOBAL_VIDEO_ERROR,
  SET_GLOBAL_VIDEO_PANEL,
  SET_GLOBAL_VIDEO_RETRY_ERROR,
  SET_GLOBAL_VIDEO_PLAYER_TYPE,
  SET_GLOBAL_VIDEO_SCREEN_IN_CONTROL_OF_STATE,
  SET_GLOBAL_VIDEO_SHOULD_RESET_STILL_WATCHING,
  SET_GLOBAL_VIDEO_SHOW_CONTROLS,
  SET_GLOBAL_VIDEO_STARTED_PLAYBACK,
  SET_GLOBAL_VIDEO,
  SET_GLOBAL_STREAM_EXIT,
  SET_GLOBAL_PERCENT_WATCHED_50_PERCENT,
} from './Actions';

const INITIAL_STATE = {
  seo: null,
  errors: null,
  video: null,
  panelVideo: null,
  releaseUrl: null,
  restartVideo: null,
  isLiveRestart: false,
  isStatic: false,
  hideVideoUntilPlayback: false,
  disableRender: true,
  nextVideos: [],
  nextVideosValidFor: null,
  page: null,
  percentWatched: 0,
  playerType: null,
  portalRef: '',
  isPortalContentFinished: false,
  screenInControlOfState: null,
  setStickyControls: () => {},
  setPlayerDataHover: () => {},
  shouldResetStillWatching: false,
  startedPlayback: false,
  streamExit: false,
  retryErrors: {},
  videoIsLive: false,
  vodContentSet: false,
  hasSeenHalfAnyVideo: false,
};

export default function(state = INITIAL_STATE, action) {
  const errors = determineErrorFromResponse(action);
  const payload = _.get(action, 'payload');

  switch (action.type) {
    case SET_GLOBAL_VIDEO_PANEL: {
      const { screenInControlOfState, isLiveRestart } = state;
      const page = _.get(payload, 'source');
      const ignoreUpdatingState = screenInControlOfState && screenInControlOfState !== page;
      if (ignoreUpdatingState) return state;
      const panel = _.get(payload, 'panel');
      const videoType = _.get(panel, 'videoType');
      const type = _.get(panel, '@type');
      const isFullEpisode = isEpisode(videoType);
      const videoIsLive = isLive(type) && !isLiveRestart;
      const videoFormat = videoIsLive ? LIVE : VOD;
      const panelVideo = panel && { ...panel, isFullEpisode, videoFormat };
      const playerContext = videoIsLive ? PLAYER_CONTEXT_LIVE : PLAYER_CONTEXT_WATCH;
      const playerScreenUrl = _.get(panelVideo, 'playerScreenUrl', false);
      const isVodUnavailable = !videoIsLive && !playerScreenUrl;

      const nextState = {
        ...state,
        errors,
        isStatic: isStaticRender(),
        isVodUnavailable,
        page,
        panelVideo,
        playerContext,
        releaseUrl: null,
        video: null,
      };

      return nextState;
    }
    case FETCH_GLOBAL_VIDEO_PANEL: {
      const data = _.get(payload, 'data');
      const seo = _.get(data, 'SEO');
      const videoData = _.get(data, 'panels.member.0.items.member.0') || _.get(state, 'video');
      const videoType = _.get(videoData, 'videoType');
      const isFullEpisode = isEpisode(videoType);
      const videoFormat = isLive(videoData) ? LIVE : VOD;
      const panelVideo = videoData && { ...videoData, isFullEpisode, videoFormat };
      const nextState = {
        ...state,
        panelVideo,
        seo,
        errors,
        isStatic: isStaticRender(),
      };

      return nextState;
    }
    case SET_GLOBAL_VIDEO_DISABLE_RENDER: {
      const nextState = {
        ...state,
        disableRender: _.get(payload, 'value'),
        playerType: _.get(payload, 'playerType', PLAYER_TYPE_DEFAULT),
      };
      const context = _.get(payload, 'context');
      if (context) nextState.playerContext = context;
      return nextState;
    }
    case SET_GLOBAL_VIDEO_STARTED_PLAYBACK: {
      return {
        ...state,
        startedPlayback: _.get(payload, 'value'),
      };
    }
    case SET_GLOBAL_VIDEO_SHOW_CONTROLS: {
      return {
        ...state,
        showControls: _.get(payload, 'value'),
      };
    }
    case FETCH_GLOBAL_VIDEO_UP_NEXT: {
      const videos = _.get(payload, 'data.member', {});
      const upNextHeadline = payload?.data?.headline ?? '';
      const nextVideosValidFor = _.get(payload, 'data.validFor', null);
      const nextVideos = Object.values(videos);
      return {
        ...state,
        nextVideos,
        nextVideosValidFor,
        upNextHeadline,
      };
    }
    case FETCH_GLOBAL_VIDEO_NEXT_VIDEO: {
      const nextVideo = _.get(payload, 'data');
      return {
        ...state,
        nextVideo,
      };
    }
    case FETCH_GLOBAL_VIDEO: {
      return {
        ...state,
        vodContentSet: false,
      };
    }
    case SET_GLOBAL_PORTAL_REF: {
      return {
        ...state,
        portalRef: payload || '',
      };
    }
    case SET_GLOBAL_PORTAL_CONTENT_FINISHED: {
      return {
        ...state,
        isPortalContentFinished: payload,
      };
    }
    case SET_GLOBAL_VIDEO: {
      if (!state.panelVideo) return state;
      const videoType = _.get(payload, 'videoType');
      const isFullEpisode = isEpisode(videoType);
      const videoIsLive = isLive(_.get(payload, '@type'));
      const videoFormat = videoIsLive ? LIVE : VOD;
      const defaultPercentWatched = _.get(state, 'panelVideo.percentWatched', 0);
      const percentWatched = _.get(state, 'percentWatched') || defaultPercentWatched;
      const displaySeasonAndEpisodeCounts = _.get(
        state,
        'panelVideo.displaySeasonAndEpisodeCounts',
        true,
      );
      const video = payload
        ? {
            ...payload,
            displaySeasonAndEpisodeCounts,
            isFullEpisode,
            percentWatched,
            videoFormat,
          }
        : null;
      const releaseUrl = _.get(video, 'playerScreenUrl', _.get(video, 'livePlayerScreenUrl', null));

      return {
        ...state,
        releaseUrl,
        errors,
        video,
        nextVideos: [],
        videoIsLive,
        vodContentSet: true,
      };
    }
    case PLAY_GLOBAL_VIDEO_NEXT_PLAYLIST_VIDEO: {
      const video = state.nextVideo;
      const releaseUrl = _.get(video, 'url');
      const nextVideos = state.nextVideos.slice();
      nextVideos.pop();
      return {
        ...state,
        video,
        nextVideo: null,
        nextVideos,
        releaseUrl,
        startedPlayback: false,
      };
    }
    case SET_GLOBAL_VIDEO_PLAYER_TYPE: {
      const playerType = payload;
      if (!playerType) return state;

      return {
        ...state,
        playerType,
      };
    }
    case CLEAR_GLOBAL_VIDEO: {
      return {
        ...INITIAL_STATE,
        disableRender: state.disableRender,
        isLiveRestart: state.isLiveRestart,
        playerType: state.playerType,
        restartVideo: state.restartVideo,
        screenInControlOfState: state.screenInControlOfState,
      };
    }
    case SET_GLOBAL_LIVE_RESTART: {
      const restartVideo = _.get(action, 'payload.data');

      return {
        ...state,
        isLiveRestart: true,
        restartVideo,
      };
    }
    case CLEAR_GLOBAL_LIVE_RESTART: {
      return {
        ...state,
        isLiveRestart: _.get(INITIAL_STATE, 'isLiveRestart'),
        restartVideo: _.get(INITIAL_STATE, 'restartVideo'),
      };
    }
    case SET_GLOBAL_VIDEO_ERROR: {
      const newErrors = payload;
      if (!newErrors) return state;
      return {
        ...state,
        errors: newErrors,
      };
    }
    case SET_GLOBAL_VIDEO_RETRY_ERROR: {
      const retryErrors = payload;
      if (!retryErrors) return state;
      return {
        ...state,
        retryErrors,
      };
    }
    case SET_GLOBAL_VIDEO_SHOULD_RESET_STILL_WATCHING: {
      const shouldResetStillWatching = payload;
      return {
        ...state,
        shouldResetStillWatching,
      };
    }
    case CLEAR_GLOBAL_NEXT_VIDEOS_VALID_FOR: {
      return {
        ...state,
        nextVideosValidFor: _.get(INITIAL_STATE, 'nextVideosValidFor'),
      };
    }
    case SET_GLOBAL_VIDEO_SCREEN_IN_CONTROL_OF_STATE: {
      return {
        ...state,
        screenInControlOfState: payload,
      };
    }
    case SET_GLOBAL_STREAM_EXIT: {
      const streamExit = payload;
      return {
        ...state,
        streamExit,
        startedPlayback: false,
      };
    }
    case SAVE_GLOBAL_PERCENT_WATCHED: {
      const percentWatched = _.get(state, 'panelVideo.percentWatched', 0);
      return {
        ...state,
        percentWatched,
      };
    }
    case CLEAR_GLOBAL_PERCENT_WATCHED: {
      const percentWatched = _.get(INITIAL_STATE, 'percentWatched');
      return {
        ...state,
        percentWatched,
      };
    }
    case HANDLE_VOD_SECTION_CLICKED: {
      return {
        ...state,
        ...payload,
        vodSectionClicked: true,
      };
    }
    case RESET_VOD_SECTION_CLICKED: {
      return {
        ...state,
        originalItem: null,
        vodClickedState: {},
        vodSectionClicked: false,
      };
    }
    case SET_GLOBAL_PERCENT_WATCHED_50_PERCENT: {
      optimizelyUserAttributeEvents().video50PercentWatching(true);
      return {
        ...state,
        hasSeenHalfAnyVideo: true,
      };
    }
    default:
      return state;
  }
}
